import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faPen, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { EM_DASH, formatLastActive, formatMonthDay } from '@lib/format_date';
import { getDisplayRole, isSameRoleFamily } from '@hooks/usePartnerRoles';
import type { DashboardMember } from '@hooks/requests/queries/use_partner_team_query';
import type { PendingInvitation } from '@hooks/requests/queries/use_partner_team_invitations_query';
import { Avatar, RoleTag, RowMenu } from '../ui';
import { avatarColorIndex } from '../ui/avatar_color';

type SortField = 'name' | 'email' | 'role' | 'lastActive';
type SortDirection = 'asc' | 'desc';

interface TeamTableProps {
  members: DashboardMember[];
  invitations?: PendingInvitation[];
  currentUserId?: string | null;
  callerIsAceUpStaff?: boolean;
  isLoading?: boolean;
  error?: Error | null;
  onInvite?: () => void;
  onRevokeInvitation?: (invitationId: string) => void;
  onEditRole?: (member: DashboardMember) => void;
  onRemoveMember?: (member: DashboardMember) => void;
}

const memberLabel = (member: DashboardMember): string =>
  member.name || member.email || member.userId;

const sortValue = (member: DashboardMember, field: SortField): string | number => {
  switch (field) {
    case 'name':
      return memberLabel(member).toLowerCase();
    case 'email':
      return (member.email || '').toLowerCase();
    case 'role':
      return (getDisplayRole(member.roles) || '').toLowerCase();
    case 'lastActive':
      // Never-logged-in members sort as "longest ago" in either direction.
      return member.lastActive ? new Date(member.lastActive).getTime() : 0;
    default:
      return '';
  }
};

/**
 * Last Active for a member who has never signed in.
 *
 * A *member* who has never logged in (added directly in the Auth0 Dashboard)
 * renders with an em dash, not "Invited" — that label is reserved for rows
 * backed by a real pending invitation (BR-008). This table never derives
 * "invited" from a missing last-active date any more; see `invitations`.
 */
const lastActiveCell = (member: DashboardMember): string => formatLastActive(member.lastActive);

const COLUMNS: { field: SortField; label: string }[] = [
  { field: 'name', label: 'Name' },
  { field: 'email', label: 'Email' },
  { field: 'role', label: 'Role' },
  { field: 'lastActive', label: 'Last Active' },
];

/**
 * The organization's dashboard roster (Account page → Team; Figma 699:2888 and
 * 2436:8769). Admin-only; the page decides whether to render it at all.
 *
 * The design opens sorted by Last Active, descending — which is also the order
 * the API returns — so that is the initial state rather than leaving the header
 * row with no sorted column.
 */
const TeamTable: React.FC<TeamTableProps> = ({
  members,
  invitations = [],
  currentUserId,
  callerIsAceUpStaff = false,
  isLoading = false,
  error = null,
  onInvite,
  onRevokeInvitation,
  onEditRole,
  onRemoveMember,
}) => {
  const [sortField, setSortField] = useState<SortField>('lastActive');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const orderedMembers = useMemo(() => {
    const pinned = members.filter((member) => member.userId === currentUserId);
    const rest = members.filter((member) => member.userId !== currentUserId);

    const sorted = [...rest].sort((a, b) => {
      const aValue = sortValue(a, sortField);
      const bValue = sortValue(b, sortField);
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return [...pinned, ...sorted];
  }, [members, currentUserId, sortField, sortDirection]);

  // BR-008: invitations render after every member row — they have no
  // last-active to sort by — most-recently-invited first among themselves.
  // Appended to the member ordering above, never interleaved with it.
  const orderedInvitations = useMemo(
    () =>
      [...invitations].sort((a, b) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      }),
    [invitations]
  );

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  const countLabel =
    error || isLoading
      ? EM_DASH
      : `${members.length} ${members.length === 1 ? 'member' : 'members'}`;

  const bar = (
    <div className="api-keys-bar">
      <span className="api-keys-bar-count" data-testid="team-member-count">
        {countLabel}
      </span>
      <button
        type="button"
        className="api-keys-new-key-btn"
        onClick={onInvite}
        disabled={!onInvite}
      >
        <span aria-hidden="true">+</span> Invite member
      </button>
    </div>
  );

  const message = (text: string) => (
    <div>
      {bar}
      <p className="team-status-message">{text}</p>
    </div>
  );

  if (error) return message('Team details are unavailable right now. Please try again.');
  if (isLoading) return message('Loading team…');
  if (members.length === 0 && orderedInvitations.length === 0) {
    return message('No other members have dashboard access yet.');
  }

  return (
    <div>
      {bar}

      <div className="api-keys-table-wrap overflow-x-auto">
        <table className="api-keys-table team-table">
          <thead>
            <tr>
              {COLUMNS.map(({ field, label }) => {
                const isSorted = sortField === field;
                return (
                  <th key={field} className={isSorted ? 'is-sorted' : undefined}>
                    <button
                      type="button"
                      onClick={() => handleSort(field)}
                      aria-label={`Sort by ${label}`}
                      className="team-sort-btn"
                    >
                      {label}
                      {/* Only the sorted column carries an arrow, and it points
                          the way that column is actually sorted. */}
                      {isSorted && (
                        <FontAwesomeIcon
                          icon={sortDirection === 'asc' ? faChevronUp : faChevronDown}
                          className="team-sort-icon"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </th>
                );
              })}
              {/* Reserved for the per-row actions menu. */}
              <th aria-label="Row actions" className="api-keys-cell-actions" />
            </tr>
          </thead>
          <tbody>
            {orderedMembers.map((member) => {
              const displayRole = getDisplayRole(member.roles);
              const isCurrentUser = member.userId === currentUserId;
              const sameFamily = isSameRoleFamily(callerIsAceUpStaff, member.roles);
              return (
                <tr key={member.userId} className="api-keys-row team-row" data-testid="team-row">
                  <td>
                    <div className="team-cell-member">
                      <Avatar
                        size="sm"
                        name={memberLabel(member)}
                        colorIndex={avatarColorIndex(member.userId)}
                      />
                      <span className="team-cell-name">
                        {memberLabel(member)}
                        {isCurrentUser && <span className="team-cell-you"> (you)</span>}
                      </span>
                    </div>
                  </td>
                  <td>{member.email || EM_DASH}</td>
                  <td>{displayRole ? <RoleTag displayRole={displayRole} /> : EM_DASH}</td>
                  <td>{lastActiveCell(member)}</td>
                  <td aria-label="Row actions" className="api-keys-cell-actions">
                    {!isCurrentUser && sameFamily && onEditRole && (
                      <RowMenu
                        menuClassName="api-keys-menu"
                        triggerLabel={`Actions for ${memberLabel(member)}`}
                        items={[
                          { label: 'Edit role', icon: faPen, onClick: () => onEditRole(member) },
                          {
                            label: 'Remove member',
                            icon: faTrash,
                            variant: 'danger',
                            onClick: () => onRemoveMember?.(member),
                          },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
            {orderedInvitations.map((invitation) => {
              const displayRole = getDisplayRole([invitation.role]);
              const sameFamily = isSameRoleFamily(callerIsAceUpStaff, [invitation.role]);
              return (
                <tr
                  key={invitation.invitationId}
                  className="api-keys-row team-row"
                  data-testid="team-row-invited"
                >
                  <td>
                    <div className="team-cell-member">
                      <Avatar
                        size="sm"
                        name={invitation.email}
                        colorIndex={avatarColorIndex(invitation.invitationId)}
                        muted
                      />
                      <span className="team-cell-name team-cell-name--invited">
                        {invitation.email}
                      </span>
                    </div>
                  </td>
                  <td>{invitation.email}</td>
                  <td>{displayRole ? <RoleTag displayRole={displayRole} /> : invitation.role}</td>
                  <td>Invited on {formatMonthDay(invitation.createdAt)}</td>
                  <td aria-label="Row actions" className="api-keys-cell-actions">
                    {sameFamily && onRevokeInvitation && (
                      <RowMenu
                        menuClassName="api-keys-menu"
                        triggerLabel={`Actions for ${invitation.email}`}
                        items={[
                          {
                            label: 'Revoke invitation',
                            variant: 'danger',
                            onClick: () => onRevokeInvitation(invitation.invitationId),
                          },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeamTable;
